/* istanbul ignore file -- @preserve */
import { useCallback, useEffect, useRef, useState } from 'react';

export function useTypewriterMessage(fullContent: string, baseSpeed = 20): string {
  const displayedRef = useRef('');
  const [displayed, setDisplayed] = useState('');
  const rafRef = useRef<number | null>(null);
  const lastTimeRef = useRef(0);

  const tick = useCallback(
    (now: number) => {
      const pending = fullContent.slice(displayedRef.current.length);
      if (pending.length === 0) {
        rafRef.current = null;
        return;
      }

      const qLen = pending.length;
      const delay = qLen > 100 ? 5 : qLen > 30 ? 10 : baseSpeed;
      const chars = qLen > 100 ? 5 : qLen > 30 ? 3 : 1;

      if (now - lastTimeRef.current >= delay) {
        displayedRef.current += pending.slice(0, chars);
        setDisplayed(displayedRef.current);
        lastTimeRef.current = now;
      }

      rafRef.current = requestAnimationFrame(tick);
    },
    [fullContent, baseSpeed],
  );

  useEffect(() => {
    // Reset when content shrinks (e.g. clear() resets messages)
    if (fullContent.length < displayedRef.current.length) {
      displayedRef.current = '';
      setDisplayed('');
    }

    // Start or continue the animation loop whenever fullContent grows
    if (displayedRef.current.length < fullContent.length && !rafRef.current) {
      lastTimeRef.current = performance.now();
      rafRef.current = requestAnimationFrame(tick);
    }

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [fullContent, tick]);

  return displayed;
}
